import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiPaths } from '../../../service/apiPaths';
import ApiService from '../../../service/ApiService';
import {
    formatDateToISODateString, limit, updateComponent, ViewType
} from '../../../util/constant';
import { ILeave } from './Leave.props';

interface ILeaveView {
  _id: string;
  name: string;
  description: string;
  status: string;
  totalHours: number;
  users: Array<{
    name: string;
    email: string;
    role: string;
    totalHours: number;
    dateWise: Array<{
      date: string;
      hours: number;
    }>;
  }>;
}

const useLeave = () => {
  const [selectedLeaveId, setSelectedLeaveId] = useState<string | null>(
    null
  );
  const [selectedLeaveView, setSelectedLeaveView] =
    useState<ILeaveView | null>(null);
  const [leaveData, setLeaveData] = useState<ILeave[]>([]);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false); // State to manage the visibility of the confirmation dialog
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [years, setYears] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [userLeave, setUserLeave] = useState<any>();
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedYear && selectedMonth) {
      handleDateFilter(null, null);
    }
  }, [selectedYear, selectedMonth]);
  useEffect(() => {
    if (selectedYear) {
      fetchUserLeave();
    }
  }, [selectedYear]);
  // Fetch years from API
  const fetchUserLeave = async () => {
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getLeaveUser}/${selectedYear}`,
      });
      setUserLeave(result);
    } catch (error) {
      console.error("Error fetching user leave:", error);
    }
  };
  const fetchYears = async () => {
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getYears}`,
      });

      // Handle the year format ["2024-2025"]
      const yearsList = Array.isArray(result) ? result : [];
      setYears(yearsList);

      // Set default year based on current date
      if (yearsList.length > 0) {
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1; // 1-12
        const currentYear = currentDate.getFullYear();

        // If current month is Nov or Dec, we're in the next working year
        // e.g., Nov 2024 is part of 2024-2025 working year
        const workingYear =
          currentMonth >= 11
            ? `${currentYear}-${currentYear + 1}`
            : `${currentYear - 1}-${currentYear}`;

        // Find the matching working year or use the latest available year
        const defaultYear =
          yearsList.find((year) => year === workingYear) ||
          yearsList[yearsList.length - 1]; // Use the latest year if current not found
        setSelectedYear(defaultYear);
      }
    } catch (error) {
      console.error("Error fetching years:", error);
    }
  };

  // Set initial year and month
  useEffect(() => {
    fetchYears();
    // Set default month to current month
    const currentMonth = (new Date().getMonth() + 1)
      .toString()
      .padStart(2, "0");
    setSelectedMonth(currentMonth);
  }, []);

  const handleDateFilter = async (
    fromDate: Date | null,
    toDate: Date | null
  ) => {
    setLoading(true);
    try {
      let endpoint = ``;

      if (selectedYear && selectedMonth) {
        const [startYear, endYear] = selectedYear.split("-");

        const yearForMonth =
          parseInt(selectedMonth) >= 11 ? startYear : endYear;
        const yearMonth = `${yearForMonth}-${selectedMonth}`;

        endpoint = `${apiPaths.getLeave}?year=${selectedYear}&month=${yearMonth}&page=${page}&limit=${limit}`;

        if (fromDate && toDate) {
          const formattedFromDate = formatDateToISODateString(fromDate);
          const formattedToDate = formatDateToISODateString(toDate);
          endpoint += `&fromDate=${formattedFromDate}&toDate=${formattedToDate}`;
        }

        if (search) {
          endpoint += `&search=${search}`;
        }
      } else {
        endpoint = `${apiPaths.getLeave}?page=${page}&limit=${limit}`;
        if (search) {
          endpoint += `&search=${search}`;
        }
        if (fromDate && toDate) {
          const formattedFromDate = formatDateToISODateString(fromDate);
          const formattedToDate = formatDateToISODateString(toDate);
          endpoint += `&fromDate=${formattedFromDate}&toDate=${formattedToDate}`;
        }
      }

      const result = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });
      setLeaveData(result?.leaves);
      setTotalPage(result.totalPages);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmStatusUp = async () => {
    setLoading(true);
    const status = leaveData.find((leave: any) => leave._id === deletingId);
    const data = {
      status: status?.status.toString() === "Active" ? "InActive" : "Active",
    };
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateLeave}${deletingId}`,
        data: data,
      });
      setLeaveData((prevData) =>
        prevData.map((leave) =>
          leave._id === deletingId
            ? {
                ...leave,
                status:
                  leave?.status?.toString() === "Active"
                    ? "InActive"
                    : "Active",
              }
            : leave
        )
      );
    } catch (error) {
      console.error("Error deleting leave:", error);
    } finally {
      setLoading(false);
    }
    setShowConfirmation(false);
    setDeletingId(null);
  };

  const handleEdit = (id: string) => {
    setSelectedLeaveId(id);
  };

  const handleView = (leave: ILeaveView) => {
    setSelectedLeaveView(leave);
  };

  const handleStatusUp = async (id: string) => {
    setDeletingId(id);
    setShowConfirmation(true);
  };
  const handleCancelStatusUp = () => {
    setShowConfirmation(false);
    setDeletingId(null);
  };

  const handleAdd = () => {
    navigate("/addLeave");
  };
  const updateScreen = (type: ViewType) => {
    updateComponent(type, setSelectedLeaveView, setSelectedLeaveId);
  };

  return {
    selectedLeaveId,
    selectedLeaveView,
    leaveData,
    showConfirmation,
    handleAdd,
    handleEdit,
    handleView,
    handleStatusUp,
    handleCancelStatusUp,
    handleConfirmStatusUp,
    handleDateFilter,
    updateScreen,
    loading,
    setSearch,
    search,
    setPage,
    page,
    totalPage,
    years,
    selectedYear,
    setSelectedYear,
    selectedMonth,
    setSelectedMonth,
    userLeave,
  };
};

export default useLeave;
